import {
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Typography,
  Divider,
  TextField,
  Badge,
  Box,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useAppDispatch, useAppSelector } from "../hooks/hooks";
import { Contact } from "../models/Contact";
import AddIcon from "@mui/icons-material/Add";
import { UserStatusIndicator } from "./UserStatusIndicator";
import { formatLastSeen } from "../utils/formatLastSeen";
import { resetUnreadCount, setSearchQuery } from "../redux/chatListSlice";

// interface Contact {
//   id: string;
//   name: string;
//   lastMessage: string;
//   timestamp: string;
//   unread: number;
//   avatar?: string;
//   isGroup?: boolean;
//   lastSender?: string;
//   status?: "online" | "offline";
// }

const ActionsFooter = styled(Box)(({ theme }) => ({
  position: "sticky",
  bottom: 0,
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

interface ChatListProps {
  contacts: Contact[];
  onSelect: (contact: Contact) => void;
  onNewContact?: () => void;
  onNewGroup?: () => void;
}

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    right: 8,
    top: 35,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: "0 4px",
  },
}));

const ChatList = ({ onSelect, onNewContact, onNewGroup }: ChatListProps) => {
  // const searchQuery = useAppSelector((state) => state.chatList.searchQuery);
  const dispatch = useAppDispatch();

  const { filteredContacts, loading, selectedContact, searchQuery } =
    useAppSelector((state) => state.chatList);
  const { contacts: savedContacts } = useAppSelector((state) => state.contacts);

  const combinedContacts = [
    ...filteredContacts.map((contact) => ({
      ...contact,
      hasConversation: true,
    })),
    ...savedContacts
      .filter((saved) => !filteredContacts.some((c) => c.id === saved.id))
      .map((contact) => ({
        ...contact,
        hasConversation: false,
        lastMessage: "",
        unread: 0,
        lastSender: undefined,
      })),
  ];

  // const filteredContacts = combinedContacts.filter(
  //   (contact) =>
  //     contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
  //     (contact.hasConversation &&
  //       contact.lastMessage.toLowerCase().includes(searchQuery.toLowerCase()))
  // );

  const handleContactSelect = (
    contact: Contact & { hasConversation: boolean }
  ) => {
    if (!contact.hasConversation) {
      const { hasConversation, ...newContact } = contact;
      onSelect(newContact);
    } else {
      onSelect(contact);
      // Reset unread count when selecting a contact
      dispatch(resetUnreadCount({ contactId: contact.id }));
    }
  };

  return (
    <Box
      sx={{
        width: "100%",

        marginBottom: "56px",
        paddingBottom: { xs: 2, sm: 4 }, // 16px mobile, 32px desktop
        height: "calc(100dvh - 180px)", // Accounts for header if needed
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
      }}
    >
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search chats..."
          value={searchQuery}
          onChange={(e) => dispatch(setSearchQuery({ query: e.target.value }))}
          sx={{ mb: 2 }}
        />
      </Box>

      {loading && (
        <Box sx={{ textAlign: "center", p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Loading contacts...
          </Typography>
        </Box>
      )}

      {!loading && filteredContacts.length === 0 && (
        <Box
          sx={{
            textAlign: "center",
            py: 4,
            minHeight: "200px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography variant="subtitle1" color="text.secondary">
            No contacts found
          </Typography>
          {onNewContact && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onNewContact}
              sx={{ mt: 2 }}
              fullWidth
            >
              Add Contact
            </Button>
          )}
          {onNewGroup && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onNewGroup}
              sx={{ mt: 2, ml: 1 }}
              fullWidth
            >
              New Group
            </Button>
          )}
        </Box>
      )}

      {!loading && combinedContacts.length > 0 && (
        <List sx={{ width: "100%" }}>
          {combinedContacts.map((contact) => (
            <Box key={contact.id}>
              <ListItem
                onClick={() => handleContactSelect(contact)}
                sx={{
                  backgroundColor:
                    selectedContact?.id === contact.id
                      ? "action.selected"
                      : "inherit",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                  py: 1.5,
                }}
              >
                <ListItemAvatar>
                  <UserStatusIndicator contact={contact} showUnread={false} />
                  {/* <StyledBadge
                    color={contact.status === "online" ? "success" : "default"}
                    variant={contact.status === "online" ? "dot" : "standard"}
                    badgeContent={contact.unread > 0 ? contact.unread : null}
                  >
                    <Avatar
                      alt={contact.name}
                      src={contact.avatar}
                      sx={{
                        bgcolor: contact.isGroup
                          ? "secondary.main"
                          : "primary.main",
                      }}
                    >
                      {!contact.avatar && contact.name.charAt(0)}
                    </Avatar>
                  </StyledBadge> */}
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Typography
                        component="span"
                        variant="body1"
                        color="text.primary"
                        sx={{
                          fontWeight: contact.unread > 0 ? "bold" : "normal",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {contact.name}
                      </Typography>
                      {contact.hasConversation && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 0.5 }}
                        >
                          {contact.status === "online"
                            ? "Online"
                            : formatLastSeen(contact.lastOnlineTime ?? "")}
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={
                    // <Box
                    //   sx={{
                    //     display: "flex",
                    //     alignItems: "center",
                    //     justifyContent: "space-between",
                    //     gap: 1,
                    //   }}
                    // >
                    contact.hasConversation ? (
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          fontWeight: contact.unread > 0 ? "bold" : "normal",
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {contact.lastSender && `${contact.lastSender}: `}
                        {contact.lastMessage}
                      </Typography>
                    ) : (
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          fontStyle: "italic",
                          display: "block",
                        }}
                      >
                        No messages yet
                      </Typography>
                    )
                    // </Box>
                  }
                />
                {/* {contact.hasConversation && contact.unread > 0 && (
                  <Box sx={{ ml: 2 }}>
                    <Badge badgeContent={contact.unread} color="primary" />
                  </Box>
                )} */}
              </ListItem>
              <Divider variant="inset" component="li" />
            </Box>
          ))}
        </List>
      )}
      <ActionsFooter>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "space-between",
            "& .MuiButton-root": {
              flex: 1,
            },
          }}
        ></Box>
      </ActionsFooter>
    </Box>
  );
};

export default ChatList;
